import React from 'react';
import { Link } from 'react-router-dom';

import { Callout, Step } from './shared';

const StandaloneMode: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-2">Standalone Projects</h2>
    <p className="text-base-content/50 text-sm mb-5">MFlow&apos;s own built-in tracker — no external account needed.</p>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      Standalone is a full Kanban tracker that lives entirely inside MFlow. It&apos;s the right choice if you don&apos;t
      use Jira or Trello, or just want somewhere quick to track work without connecting anything.
    </p>

    <div className="space-y-3 mb-6">
      <Step n={1}>
        Go to <strong>Standalone</strong> in the sidebar and create a project — give it a name and, optionally, a short
        readable key (e.g. <code className="bg-base-200 px-1 rounded text-xs">WR</code>). If you skip the key, MFlow
        derives one from the name automatically.
      </Step>
      <Step n={2}>
        Every task gets a permanent readable id like <code className="bg-base-200 px-1 rounded text-xs">WR-12</code> —
        set once at creation, never changes, and works as a shareable link to that task.
      </Step>
      <Step n={3}>
        Add tasks to any column, set a priority, due date, and labels, and drag them between columns. Drag-and-drop
        position is purely visual — it&apos;s independent of priority, so a low-priority task can still sit above a
        high-priority one.
      </Step>
    </div>

    <h3 className="font-semibold mb-2">Custom statuses &amp; labels</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      Unlike a fixed To Do / In Progress / Done board, every Standalone project&apos;s columns are fully yours to
      configure from <strong>Board settings</strong>: add, rename, reorder, or delete statuses, and tag each one as{' '}
      <strong>To Do</strong>, <strong>In Progress</strong>, or <strong>Done</strong> so summary counts and analytics
      still make sense. A status still holding tasks can&apos;t be deleted until you say where those tasks should move,
      and a project&apos;s last remaining status can never be deleted. Labels work the same way — create as many as you
      like from the board; deleting one removes it from every task that had it.
    </p>

    <h3 className="font-semibold mb-2">Subtasks, comments &amp; attachments</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      Any task can have subtasks (unlimited nesting via a parent task) — a parent card shows a progress badge like{' '}
      <code className="bg-base-200 px-1 rounded text-xs">2/5</code> based on how many of its subtasks are in a Done
      status. Open a task to add comments (add and delete, no editing after posting) or attach files and images.
    </p>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      A task&apos;s page lists its subtasks. Each row shows the subtask&apos;s key (like{' '}
      <code className="bg-base-200 px-1 rounded text-xs">MF-42</code>), its title, and its current status with the
      column&apos;s color. Click a row to open that subtask. You can also add a new subtask from the same list. On any
      task, the <strong>Parent task</strong> field shows the parent&apos;s key and title. Click it to move the task
      under a different parent, or to make it a top-level task. MFlow won&apos;t let you pick one of the task&apos;s own
      subtasks as its parent.
    </p>

    <h3 className="font-semibold mb-2">Sharing a project</h3>
    <p className="text-sm text-base-content/70 mb-3 leading-relaxed">
      Standalone projects can be shared, so a teammate works on the same board as you. Sharing is available for
      Standalone projects only, not Jira or Trello.
    </p>
    <div className="space-y-3 mb-4">
      <Step n={1}>
        Open <strong>Board settings → Members</strong>, enter your teammate&apos;s email, choose a role, and click
        invite. They get an email, and until they respond they&apos;re listed as <em>Invited — waiting to accept</em>.
      </Step>
      <Step n={2}>
        Your teammate signs in to MFlow with that email. The invitation appears under{' '}
        <strong>Project invitations</strong> at the top of the main dashboard, the page they land on after signing in,
        and also at the top of the <strong>Standalone</strong> page. <strong>Accept</strong> opens the board, and{' '}
        <strong>Decline</strong> removes the invitation. If they&apos;re new to MFlow, the invitation shows up before
        first-time onboarding, so it isn&apos;t hidden.
      </Step>
    </div>
    <div className="overflow-x-auto mb-4">
      <table className="table table-sm text-sm">
        <thead>
          <tr>
            <th>Role</th>
            <th>Can do</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-medium">Owner</td>
            <td className="text-base-content/70">
              Everything on the board, plus invite and remove members. The project creator is its first owner.
            </td>
          </tr>
          <tr>
            <td className="font-medium">Member</td>
            <td className="text-base-content/70">
              Work on the shared board and its tasks. Can leave the project, but can&apos;t invite anyone.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Callout type="info">
      A project always has at least one owner, so the last owner can&apos;t leave or be removed. Usage limits such as
      coding-agent tasks are still counted per person, not shared across the project.
    </Callout>

    <h3 className="font-semibold mb-2 mt-5">Coding-agent columns</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      In <strong>Board settings → Columns</strong>, an <em>In progress</em>-type column can be given a coding-agent
      role. <strong>Working</strong> is where a linked task goes while the Coding Agent runs, and{' '}
      <strong>Review</strong> is where it goes when the agent finishes. See{' '}
      <Link to="/docs/coding-agent" className="link link-primary">
        Coding Agent
      </Link>
      .
    </p>

    <h3 className="font-semibold mb-2">Analytics</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      Open <strong>Analytics</strong> from a project&apos;s menu for the same kind of health metrics Jira projects get:
      PSI, throughput, average cycle time, reopen rate, delivery accuracy, current workload (WIP), a bottleneck
      breakdown showing which status tasks are stuck in right now, and a forecast completion date extrapolated from
      recent throughput. Since Standalone has no background sync, all of this is computed fresh every time you open the
      page — there&apos;s nothing to wait on.
    </p>

    <Callout type="tip">
      A brand-new project or a quiet one with little history will show modest or zero values for most of these —
      that&apos;s expected, not an error. Cycle time and reopen rate specifically need at least one completed task to
      mean anything.
    </Callout>

    <h3 className="font-semibold mb-2 mt-5">Talking to the Standalone agent</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      Open <strong>Ask mFlow</strong> from a Standalone project or board to create, update, or delete tasks in plain
      language — &quot;create a bug for the broken checkout button&quot; or &quot;move WR-12 to done&quot; both work.
      Managing statuses, labels, subtasks, and comments themselves is done from the board UI directly, not through chat,
      at least for now.
    </p>

    <Callout type="info">
      Outgrown Standalone, or want to move a project the other way? See{' '}
      <Link to="/docs/transfer" className="link link-primary">
        Project Transfer
      </Link>{' '}
      — it copies a whole Standalone project into Jira or Trello (or back), remapping statuses as it goes.
    </Callout>
  </div>
);

export default StandaloneMode;
